import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalculateIcon from '@mui/icons-material/Calculate';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BarChartIcon from '@mui/icons-material/BarChart';
import { apiFetch } from '../../api/client';
import { withSetorScope } from '../../utils/dataIsolation';
import TrilhaContratoEvolucaoChart from './TrilhaContratoEvolucaoChart';

import { formatCurrencyBr } from '../../utils/parseValorMoeda';

const formatCurrency = formatCurrencyBr;

const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);

function Campo({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );
}

function BlocoTimeline({ node, itens }) {
  const isOriginal =
    node.tipo === 'contrato_original' ||
    node.tipo === 'contrato_primitivo' ||
    Number(node.numeroAditivo) === 0;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: isOriginal ? 'primary.light' : 'divider',
        bgcolor: isOriginal ? 'rgba(25,118,210,0.04)' : 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        <Chip
          size="small"
          label={isOriginal ? 'Contrato Primitivo' : `Aditivo ${node.numeroAditivo}`}
          color={isOriginal ? 'primary' : 'secondary'}
        />
        {node.variacaoPercentual != null && (
          <Chip
            size="small"
            variant="outlined"
            label={`Variação: ${fmtPct(node.variacaoPercentual)}`}
            color={node.variacaoPercentual > 0 ? 'warning' : node.variacaoPercentual < 0 ? 'success' : 'default'}
          />
        )}
      </Stack>

      {node.observacao && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {node.observacao}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Campo label="Valor/mês anterior" value={formatCurrency(node.valorAnteriorNumerico ?? node.valorAnterior)} />
        <Campo label="Valor/mês" value={formatCurrency(node.valorMesNumerico ?? node.valorMes)} />
        <Campo label="Vigência início" value={node.vigenciaInicio} />
        <Campo label="Vigência fim" value={node.vigenciaFim} />
        <Campo label="Assinatura" value={node.assinatura} />
        <Campo label="Objeto" value={node.objeto} />
        <Campo label="Setor" value={node.setor} />
        {!isOriginal && <Campo label="Parcelas" value={node.parcelas} />}
      </Grid>

      {itens?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {isOriginal ? 'Itens do contrato' : 'Itens do aditivo'}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell align="right">R$/unidade</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.descricao}</TableCell>
                    <TableCell align="right">{formatCurrency(it.valor_unitario)}</TableCell>
                    <TableCell align="right">{formatCurrency(it.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
}

export default function LinhaDoTempoAditivosPanel({ scopeOpts }) {
  const [inputBusca, setInputBusca] = useState('');
  const [opcoes, setOpcoes] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);
  const [contratoSel, setContratoSel] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [erro, setErro] = useState('');
  const [modoIndice, setModoIndice] = useState('menor');
  const [indiceManual, setIndiceManual] = useState('');
  const [relatorio, setRelatorio] = useState(null);
  const [loadingReajuste, setLoadingReajuste] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingOpcoes(true);
      try {
        const q = encodeURIComponent(inputBusca.trim());
        const path = withSetorScope(
          `/api/contratos/comparativo/buscar-contratos?q=${q}&limit=40`,
          scopeOpts
        );
        const res = await apiFetch(path);
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setOpcoes(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!cancelled) setOpcoes([]);
      } finally {
        if (!cancelled) setLoadingOpcoes(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputBusca, scopeOpts]);

  const mergedOptions = useMemo(() => {
    const map = new Map();
    [...opcoes, ...(contratoSel ? [contratoSel] : [])].forEach((o) => {
      const key = String(o.id || o.numeroContrato);
      if (!map.has(key)) map.set(key, o);
    });
    return [...map.values()];
  }, [opcoes, contratoSel]);

  const carregarTimeline = useCallback(async (prestadorId) => {
    if (!prestadorId) {
      setTimeline(null);
      return;
    }
    setLoadingTimeline(true);
    setErro('');
    setRelatorio(null);
    try {
      const path = withSetorScope(`/api/contratos/comparativo/${prestadorId}/timeline`, scopeOpts);
      const res = await apiFetch(path);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar timeline.');
      setTimeline(data);
    } catch (e) {
      setErro(e.message);
      setTimeline(null);
    } finally {
      setLoadingTimeline(false);
    }
  }, [scopeOpts]);

  useEffect(() => {
    if (contratoSel?.id) carregarTimeline(contratoSel.id);
    else setTimeline(null);
  }, [contratoSel?.id, carregarTimeline]);

  const calcularReajuste = async () => {
    if (!contratoSel?.id) return;
    setLoadingReajuste(true);
    setErro('');
    try {
      const path = withSetorScope(
        `/api/contratos/comparativo/${contratoSel.id}/reajuste-indices`,
        scopeOpts
      );
      const body = {
        modoIndice,
        incluirResumoIA: true,
      };
      if (modoIndice === 'manual' && indiceManual !== '') {
        body.indiceManualPercent = Number.parseFloat(String(indiceManual).replace(',', '.'));
      }
      const res = await apiFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao calcular reajuste.');
      setRelatorio(data.relatorio);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoadingReajuste(false);
    }
  };

  const situacaoColor = {
    sem_aumento: 'info',
    acima_indice: 'warning',
    economia_vs_indice: 'success',
    projecao_fixo: 'info',
    projecao_itens: 'info',
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(21,101,192,0.15)',
        backgroundColor: 'rgba(255,255,255,0.92)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <TimelineIcon color="primary" fontSize="small" />
        <Typography variant="h6" fontWeight={700} color="primary">
          Trilha do contrato
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Busque por nº do contrato ou nome do fornecedor. A linha do tempo exibe valores reais do original e de cada
        aditivo, com projeção de reajuste por IPCA/IGP-M.
      </Typography>

      <Autocomplete
        options={mergedOptions}
        value={contratoSel}
        loading={loadingOpcoes}
        filterOptions={(x) => x}
        onInputChange={(_, v, reason) => {
          if (reason === 'input' || reason === 'clear') setInputBusca(v);
        }}
        onChange={(_, v) => setContratoSel(v)}
        getOptionLabel={(o) => o.label || `${o.numeroContrato || ''} — ${o.empresa || ''}`}
        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
        noOptionsText={loadingOpcoes ? 'Buscando…' : 'Nenhum contrato encontrado'}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Buscar contrato"
            placeholder="Nº do contrato ou nome do fornecedor"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingOpcoes ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 2 }}
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      {!contratoSel ? (
        <Alert severity="info">Selecione um contrato para visualizar a trilha.</Alert>
      ) : loadingTimeline ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : timeline ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{contratoSel.numeroContrato}</strong> · {contratoSel.empresa} · {contratoSel.unidade}
            <br />
            Contrato Primitivo: {formatCurrency(timeline.valorOriginalNumerico ?? timeline.valorOriginal)} · Valor
            vigente: {formatCurrency(timeline.valorVigenteNumerico ?? timeline.valorVigenteAtual)}
            {timeline.variacaoTotalPercentual != null &&
              ` · Variação total: ${fmtPct(timeline.variacaoTotalPercentual)}`}
            {' · '}
            {timeline.totalAditivos} aditivo(s)
          </Alert>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Modelo 1 — Linha do tempo (contrato / aditivos)
            </Typography>
            {timeline.evolucaoGrafico?.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<BarChartIcon />}
                onClick={() => setDashboardOpen(true)}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Dashboard — evolução
              </Button>
            )}
          </Stack>
          <Stack spacing={2} divider={<Divider flexItem />} sx={{ mb: 3 }}>
            {(timeline.nos || []).map((node, idx) => (
              <BlocoTimeline
                key={node.id || `node-${idx}`}
                node={node}
                itens={
                  node.tipo === 'contrato_original'
                    ? timeline.itensContratoPrimitivo
                    : node.id
                      ? timeline.itensPorAditivo?.[node.id]
                      : null
                }
              />
            ))}
          </Stack>

          {(timeline.isContratoVariavel || (timeline.mediaNotasFiscais?.totalNotas || 0) > 0) && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'rgba(76,175,80,0.04)' }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                Modelo 2 — Notas fiscais
              </Typography>
              {(timeline.mediaNotasFiscais?.totalNotas || 0) === 0 ? (
                <Alert severity="warning">
                  Sem NF cadastrada. O comparativo usa o valor do contrato.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Campo label="Qtd. de notas" value={timeline.mediaNotasFiscais.totalNotas} />
                    <Campo
                      label="Média valor bruto"
                      value={formatCurrency(timeline.mediaNotasFiscais.mediaBruto)}
                    />
                    <Campo
                      label="Média valor líquido"
                      value={formatCurrency(timeline.mediaNotasFiscais.mediaLiquido)}
                    />
                    <Campo
                      label="Última NF"
                      value={
                        timeline.mediaNotasFiscais.ultimaNf
                          ? `${timeline.mediaNotasFiscais.ultimaNf.numeroNota || '—'} · ${timeline.mediaNotasFiscais.ultimaNf.competencia || ''}`
                          : '—'
                      }
                    />
                    <Campo
                      label="Valor última NF (bruto)"
                      value={formatCurrency(timeline.mediaNotasFiscais.ultimaNf?.valorBruto)}
                    />
                  </Grid>

                  {(timeline.mediaNotasFiscais.porMes || []).length > 0 && (
                    <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Evolução por competência ({timeline.mediaNotasFiscais.porMes.length} meses) — clique para expandir/recolher
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Competência</TableCell>
                                <TableCell>Nº NF</TableCell>
                                <TableCell align="right">Qtd.</TableCell>
                                <TableCell align="right">Total bruto</TableCell>
                                <TableCell align="right">Total líquido</TableCell>
                                <TableCell align="right">% vs mês anterior</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {timeline.mediaNotasFiscais.porMes.map((m) => (
                                <TableRow key={m.competencia}>
                                  <TableCell>{m.competencia || '—'}</TableCell>
                                  <TableCell>
                                    {(m.numerosNota || []).join(', ') || '—'}
                                  </TableCell>
                                  <TableCell align="right">{m.qtd}</TableCell>
                                  <TableCell align="right">{formatCurrency(m.totalBruto)}</TableCell>
                                  <TableCell align="right">{formatCurrency(m.totalLiquido || null)}</TableCell>
                                  <TableCell align="right">
                                    {m.variacaoPercentual == null ? (
                                      '—'
                                    ) : (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={fmtPct(m.variacaoPercentual)}
                                        color={
                                          m.variacaoPercentual > 0
                                            ? 'warning'
                                            : m.variacaoPercentual < 0
                                              ? 'success'
                                              : 'default'
                                        }
                                      />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(timeline.mediaNotasFiscais.porMes || []).some((m) => m.qtd > 1) && (
                    <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Detalhe das notas (meses com mais de 1 NF) — clique para expandir
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <TableContainer sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Competência</TableCell>
                                <TableCell>Nº NF</TableCell>
                                <TableCell align="right">Valor bruto</TableCell>
                                <TableCell align="right">Valor líquido</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {timeline.mediaNotasFiscais.notas
                                .filter((nf) => {
                                  const mes = (timeline.mediaNotasFiscais.porMes || []).find(
                                    (m) => m.competencia === nf.competencia
                                  );
                                  return mes && mes.qtd > 1;
                                })
                                .map((nf) => (
                                  <TableRow key={nf.id}>
                                    <TableCell>{nf.competencia || '—'}</TableCell>
                                    <TableCell>{nf.numeroNota || '—'}</TableCell>
                                    <TableCell align="right">{formatCurrency(nf.valorBruto)}</TableCell>
                                    <TableCell align="right">{formatCurrency(nf.valorLiquido)}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </>
              )}
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CalculateIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Projeção de reajuste (IPCA / IGP-M)
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Índice</InputLabel>
              <Select
                label="Índice"
                value={modoIndice}
                onChange={(e) => setModoIndice(e.target.value)}
              >
                <MenuItem value="menor">Menor (IPCA ou IGP-M)</MenuItem>
                <MenuItem value="IPCA">IPCA</MenuItem>
                <MenuItem value="IGPM">IGP-M</MenuItem>
                <MenuItem value="manual">Manual (% acumulado)</MenuItem>
              </Select>
            </FormControl>
            {modoIndice === 'manual' && (
              <TextField
                size="small"
                label="% acumulado manual"
                placeholder="Ex.: 4.62"
                value={indiceManual}
                onChange={(e) => setIndiceManual(e.target.value)}
                sx={{ width: 180 }}
              />
            )}
            <Button
              variant="contained"
              onClick={calcularReajuste}
              disabled={loadingReajuste}
              startIcon={loadingReajuste ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Calcular reajuste
            </Button>
          </Stack>

          {relatorio && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Alert severity={situacaoColor[relatorio.situacao] || 'info'} sx={{ mb: 2 }}>
                {relatorio.mensagem}
              </Alert>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Campo
                  label="Base da projeção"
                  value={
                    relatorio.baseOrigem === 'ultimo_aditivo'
                      ? `Último aditivo → ${relatorio.projecaoPara || 'próximo aditivo'}`
                      : `Contrato Primitivo → ${relatorio.projecaoPara || '1º aditivo'}`
                  }
                />
                <Campo
                  label="Tipo"
                  value={relatorio.tipoCalculo === 'variavel' ? 'Variável (itens)' : 'Fixo'}
                />
                <Campo
                  label="Janela do índice"
                  value={`${relatorio.periodo?.dataInicio} → ${relatorio.periodo?.dataFim} (${relatorio.periodo?.janela || '12 meses'})`}
                />
                <Campo
                  label="Índice aplicado"
                  value={`${relatorio.indices?.indiceAplicado} (${fmtPct(relatorio.indices?.indiceAcumuladoPercentual)}) — ${relatorio.indices?.fonteIndice || ''}`}
                />
                <Campo label="IPCA (12 meses)" value={fmtPct(relatorio.indices?.ipca?.acumuladoPercentual)} />
                <Campo label="IGP-M (12 meses)" value={fmtPct(relatorio.indices?.igpm?.acumuladoPercentual)} />
                <Campo label="Valor base" value={formatCurrency(relatorio.valores?.valorBase)} />
                <Campo
                  label="Valor projetado (índice)"
                  value={formatCurrency(relatorio.valores?.valorTeoricoIndice)}
                />
                <Campo
                  label="Variação teórica"
                  value={`${formatCurrency(relatorio.valores?.aumentoTeoricoIndice)} (${fmtPct(relatorio.indices?.indiceAcumuladoPercentual)})`}
                />
              </Grid>

              {relatorio.tipoCalculo === 'variavel' && Array.isArray(relatorio.itens) && relatorio.itens.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Itens — % índice em cada um (total {fmtPct(relatorio.valores?.percentualTotalItens)})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Descrição</TableCell>
                          <TableCell align="right">Valor atual</TableCell>
                          <TableCell align="right">Valor reajustado</TableCell>
                          <TableCell align="right">Δ valor</TableCell>
                          <TableCell align="right">% índice</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relatorio.itens.map((it, idx) => (
                          <TableRow key={it.id || idx}>
                            <TableCell>{it.descricao}</TableCell>
                            <TableCell align="right">{formatCurrency(it.valorAtual)}</TableCell>
                            <TableCell align="right">{formatCurrency(it.valorReajustado)}</TableCell>
                            <TableCell align="right">{formatCurrency(it.variacaoValor)}</TableCell>
                            <TableCell align="right">{fmtPct(it.variacaoPercentual)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {relatorio.resumoIA?.texto && (
                <Alert icon={<SmartToyIcon />} severity="info">
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Análise IA
                  </Typography>
                  <Typography variant="body2">{relatorio.resumoIA.texto}</Typography>
                </Alert>
              )}
            </Paper>
          )}
        </>
      ) : null}

      <Dialog open={dashboardOpen} onClose={() => setDashboardOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Dashboard — evolução do contrato</DialogTitle>
        <DialogContent dividers>
          <TrilhaContratoEvolucaoChart
            pontos={timeline?.evolucaoGrafico || []}
            numeroContrato={contratoSel?.numeroContrato}
            empresa={contratoSel?.empresa}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashboardOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
